/**
 * Pointer cursors for the drawing tools.
 *
 * The tool you picked should be the thing in your hand: a pen for the pen, a
 * pencil for the pencil. Both are drawn from the SAME Tabler outlines the
 * toolbar buttons use (`ballpen` / `pencil`), so the cursor and the button that
 * armed it are unmistakably the same tool.
 *
 * Each glyph is stroked twice — a fat white pass underneath, the dark one on top
 * — because a cursor has to stay readable over black artwork and over white
 * paper alike, and a plain black outline disappears on the first.
 *
 * The hotspot is the nib, bottom-left of the 24×24 box, so the point you press
 * is the point that draws. Every declaration ends in `crosshair`: an SVG cursor
 * is not honoured everywhere (Safari, older engines), and falling back to a
 * precise crosshair is much better than falling back to an arrow.
 */

const WHITE = 'stroke="#fff" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round" fill="none"';
const DARK = 'stroke="#18181b" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" fill="none"';

/** Both passes of one glyph, white underlay first. */
function glyph(paths) {
    const draw = (attrs) => paths.map((d) => `<path d="${d}" ${attrs}/>`).join('');
    return `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">${draw(WHITE)}${draw(DARK)}</svg>`;
}

/**
 * `data:` rather than a file: the cursor has to be available the instant the
 * tool is armed, and a request that is still in flight shows no cursor at all.
 * Encoded with encodeURIComponent — a raw `#` in a colour would end the URL.
 */
function cursorUrl(svg, hotX, hotY) {
    return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}") ${hotX} ${hotY}, crosshair`;
}

// Tabler "ballpen" — barrel with the nib at bottom-left.
const PEN_PATHS = [
    'M14 6l7 7l-4 4',
    'M5.828 18.172a2.828 2.828 0 0 0 4 0l10.586 -10.586a2 2 0 0 0 0 -2.829l-1.171 -1.171a2 2 0 0 0 -2.829 0l-10.586 10.586a2.828 2.828 0 0 0 0 4z',
    'M4 20l1.768 -1.768',
];

// Tabler "pencil" — the same orientation, so the two read as a pair.
const PENCIL_PATHS = [
    'M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4',
    'M13.5 6.5l4 4',
];

export const PEN_CURSOR = cursorUrl(glyph(PEN_PATHS), 4, 20);
export const PENCIL_CURSOR = cursorUrl(glyph(PENCIL_PATHS), 4, 20);

/**
 * The rules themselves. `!important` and the descendant selector because every
 * layer, frame and handle sets its own cursor (move, resize, not-allowed) and
 * would otherwise win over an ancestor — while a tool is armed the whole scene
 * is one drawing surface.
 */
export const DRAW_CURSOR_CSS = `
[data-draw-tool="pen"], [data-draw-tool="pen"] * { cursor: ${PEN_CURSOR} !important; }
[data-draw-tool="pencil"], [data-draw-tool="pencil"] * { cursor: ${PENCIL_CURSOR} !important; }
`;
